import React, { useEffect, useState } from "react";
import {
  CreateTeacherInput,
  TeacherData,
  UpdateTeacherInput,
} from "../model/TeacherData";
import "../pages/dashboard/Dashboard.css";

interface EditTeacherModalProps {
  isOpen: boolean;
  teacher: TeacherData | null;
  onClose: () => void;
  onCreate: (data: CreateTeacherInput) => void;
  onUpdate: (data: UpdateTeacherInput) => void;
}

const emptyForm = {
  userName: "",
  password: "",
  phoneNumber: "",
  name: "",
  email: "",
};

// The backend rejects empty strings on optional fields, so omit them.
const optional = (value: string) => value.trim() || undefined;

const EditTeacherModal: React.FC<EditTeacherModalProps> = ({
  isOpen,
  teacher,
  onClose,
  onCreate,
  onUpdate,
}) => {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    setForm({
      ...emptyForm,
      userName: teacher?.userName || "",
      phoneNumber: teacher?.phoneNumber || "",
      name: teacher?.name || "",
      email: teacher?.email || "",
    });
  }, [teacher, isOpen]);

  if (!isOpen) return null;
  const isCreateMode = !teacher;

  const set =
    (field: keyof typeof emptyForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [field]: e.target.value });

  const canSave = isCreateMode
    ? !!form.userName.trim() && !!form.password && !!form.phoneNumber.trim()
    : !!form.phoneNumber.trim();

  const handleSave = () => {
    if (!canSave) return;
    if (isCreateMode) {
      onCreate({
        userName: form.userName.trim(),
        password: form.password,
        phoneNumber: form.phoneNumber.trim(),
        name: optional(form.name),
        email: optional(form.email),
      });
    } else {
      onUpdate({
        name: optional(form.name),
        phoneNumber: optional(form.phoneNumber),
        // An empty string clears the email.
        email: form.email.trim(),
        password: form.password || undefined,
      });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isCreateMode ? "Thêm giáo viên" : "Sửa giáo viên"}
          </h2>
          <button onClick={onClose} className="modal-close-button">
            ×
          </button>
        </div>

        <div className="modal-section">
          <div className="modal-grid-2">
            <div className="modal-field">
              <label className="modal-label">Tên đăng nhập *</label>
              <input
                type="text"
                className="modal-value"
                value={form.userName}
                onChange={set("userName")}
                disabled={!isCreateMode}
                autoFocus={isCreateMode}
              />
            </div>
            <div className="modal-field">
              <label className="modal-label">
                {isCreateMode ? "Mật khẩu *" : "Mật khẩu mới"}
              </label>
              <input
                type="password"
                className="modal-value"
                placeholder={isCreateMode ? "" : "Để trống nếu không đổi"}
                value={form.password}
                onChange={set("password")}
              />
            </div>
            <div className="modal-field">
              <label className="modal-label">Họ tên</label>
              <input
                type="text"
                className="modal-value"
                value={form.name}
                onChange={set("name")}
              />
            </div>
            <div className="modal-field">
              <label className="modal-label">Số điện thoại *</label>
              <input
                type="tel"
                className="modal-value"
                value={form.phoneNumber}
                onChange={set("phoneNumber")}
              />
            </div>
            <div className="modal-field">
              <label className="modal-label">Email</label>
              <input
                type="email"
                className="modal-value"
                value={form.email}
                onChange={set("email")}
              />
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button
            onClick={onClose}
            className="modal-button modal-button-secondary"
          >
            Huỷ
          </button>
          <button
            onClick={handleSave}
            className="modal-button modal-button-primary"
            disabled={!canSave}
          >
            {isCreateMode ? "Tạo" : "Lưu"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTeacherModal;
